import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Stack, Chip, Typography, Card, alpha, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel,
} from '@mui/material';
import dayjs from 'dayjs';
import _ from 'lodash';
import EmployeeName from '../EmployeeName';
import { FA_STATUS } from 'utils/constants/FaStatus.constant';
import { formatEmployeeThaiName } from 'utils/functions/employeeName';

// dialog เลือกอนุมัติเฉพาะบางคนในชุดคำขอ — default ติ๊กอนุมัติทุกคน ผู้อนุมัติ uncheck
// คนที่ไม่ต้องการอนุมัติออกได้ก่อนกดยืนยัน (คนที่ uncheck จะถูก reject เฉพาะคนนั้น)
function ApprovalDialog({ order, open, onClose, onConfirm }) {
  const [checked, setChecked] = useState({});

  React.useEffect(() => {
    if (open && order) {
      const initial = {};
      (order.fa_lists || []).forEach((e) => { initial[e.employee] = true; });
      setChecked(initial);
    }
  }, [open, order]);

  if (!order) return null;

  const toggle = (employeeId) => setChecked((p) => ({ ...p, [employeeId]: !p[employeeId] }));

  const approvedEmployeeIds = Object.keys(checked).filter((id) => checked[id]);

  // ถ้าไม่ติ๊กใครเลย ห้ามกดยืนยัน — เพราะ backend อ่าน approvedEmployeeIds:[] เหมือนกับ
  // "ไม่ได้ระบุ" แล้ว fallback เป็นอนุมัติทุกคน (ดู fieldAllowanceRequestOrder.service.js#update)
  // ตรงข้ามกับที่ผู้ใช้ตั้งใจ (ไม่ติ๊กใครเลย = ไม่อยากอนุมัติใครเลย) ต้องกันไว้ที่ UI ชั้นนี้เลย
  const handleConfirm = () => {
    onConfirm(order.id, approvedEmployeeIds);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>เลือกพนักงานที่จะอนุมัติ</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.5}>
          {(order.fa_lists || []).map((e) => (
            <FormControlLabel
              key={e._id}
              control={<Checkbox checked={!!checked[e.employee]} onChange={() => toggle(e.employee)} />}
              label={<EmployeeName id={e.employee} name={e.employee_name} />}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button variant="contained" color="success" onClick={handleConfirm} disabled={approvedEmployeeIds.length === 0}>ยืนยันอนุมัติ</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function FieldAllowanceApprovalTable({
  faRequestOrder, onApprove, onReject, canApprove,
}) {
  const theme = useTheme();
  const [dialogOrder, setDialogOrder] = useState(null);

  const getStatusColor = (status) => FA_STATUS?.[status]?.color || 'default';
  const getStatusLabel = (status) => FA_STATUS?.[status]?.description || status;

  const rows = faRequestOrder?.rows || [];

  if (faRequestOrder?.isLoading) {
    return <Typography color="text.secondary" sx={{ p: 2 }}>กำลังโหลด...</Typography>;
  }

  if (rows.length === 0) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">ไม่มีคำขอค่าออกสนาม</Typography>
      </Card>
    );
  }

  return (
    <Card elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <TableCell>ผู้ทำรายการ</TableCell>
              <TableCell>พนักงาน</TableCell>
              <TableCell>โปรเจค</TableCell>
              <TableCell>วันที่</TableCell>
              <TableCell>เหตุผล</TableCell>
              <TableCell>สถานะ</TableCell>
              <TableCell align="center">จัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{formatEmployeeThaiName(row?.resquester)}</TableCell>
                <TableCell>
                  {_.map(row?.fa_lists, (e, idx) => (
                    <Typography key={e?._id || idx} variant="body2">
                      {idx + 1}. <EmployeeName id={e?.employee} name={e?.employee_name} />
                      {' '}
                      <Chip label={getStatusLabel(e?.status)} color={getStatusColor(e?.status)} size="small" sx={{ height: 18, fontSize: 10 }} />
                    </Typography>
                  ))}
                </TableCell>
                <TableCell>{row?.project?.project_number} — {row?.project?.name}</TableCell>
                <TableCell>{dayjs(row?.date).format('DD/MM/BBBB')}</TableCell>
                <TableCell>{row?.description}</TableCell>
                <TableCell><Chip label={getStatusLabel(row?.status)} color={getStatusColor(row?.status)} size="small" /></TableCell>
                <TableCell align="center">
                  {canApprove && (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Button size="small" variant="contained" color="success" onClick={() => onApprove(row.id)} disabled={row.status !== 'PENDING'}>
                        อนุมัติทั้งหมด
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => setDialogOrder(row)} disabled={row.status !== 'PENDING'}>
                        เลือกอนุมัติ
                      </Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => onReject(row.id)} disabled={row.status !== 'PENDING'}>
                        ปฏิเสธทั้งหมด
                      </Button>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ApprovalDialog
        order={dialogOrder}
        open={!!dialogOrder}
        onClose={() => setDialogOrder(null)}
        onConfirm={(id, approvedEmployeeIds) => {
          onApprove(id, approvedEmployeeIds);
          setDialogOrder(null);
        }}
      />
    </Card>
  );
}
